import { useCallback, useEffect, useRef } from 'react';

import { mapListingProductToBeaconResult } from '@utils/searchspringListingBeacon';

import { useSearchspringBeacon } from './useSearchspringBeacon';

/**
 * Searchspring Beacon 2.0 for category (PLP) and search (SRP) listings.
 */
export const useListingBeacon = ({ scope, listingData, enabled }) => {
  const { sendListingBeaconV2 } = useSearchspringBeacon();
  const responseId = listingData?.responseId;

  const renderSentForResponseRef = useRef(null);
  const impressionKeysRef = useRef(new Set());
  const impressionBatchRef = useRef([]);
  const impressionTimerRef = useRef(null);

  useEffect(() => {
    impressionKeysRef.current = new Set();
    impressionBatchRef.current = [];
    renderSentForResponseRef.current = null;
  }, [responseId]);

  useEffect(() => {
    if (!enabled || !responseId) {
      return undefined;
    }
    if (renderSentForResponseRef.current === responseId) {
      return undefined;
    }
    renderSentForResponseRef.current = responseId;

    let cancelled = false;
    (async () => {
      try {
        await sendListingBeaconV2({
          scope,
          eventType: 'render',
          responseId,
        });
      } catch (error) {
        if (!cancelled) {
          console.error(error.message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, responseId, scope, sendListingBeaconV2]);

  const flushImpressionBatch = useCallback(() => {
    if (!responseId || impressionBatchRef.current.length === 0) {
      return;
    }
    const resultPayloads = impressionBatchRef.current;
    impressionBatchRef.current = [];

    (async () => {
      try {
        await sendListingBeaconV2({
          scope,
          eventType: 'impression',
          responseId,
          results: resultPayloads,
          banners: [],
        });
      } catch (error) {
        console.error(error.message);
      }
    })();
  }, [responseId, scope, sendListingBeaconV2]);

  const scheduleImpressionFlush = useCallback(() => {
    if (impressionTimerRef.current) {
      clearTimeout(impressionTimerRef.current);
    }
    impressionTimerRef.current = setTimeout(() => {
      flushImpressionBatch();
      impressionTimerRef.current = null;
    }, 350);
  }, [flushImpressionBatch]);

  const onListingItemInView = useCallback(
    (item) => {
      if (!enabled || !responseId || !item || item.isBanner) {
        return;
      }

      const key = `p:${item.uid}`;
      if (impressionKeysRef.current.has(key)) {
        return;
      }
      impressionKeysRef.current.add(key);
      try {
        impressionBatchRef.current.push(mapListingProductToBeaconResult(item));
      } catch (error) {
        console.error(error.message);
        return;
      }
      scheduleImpressionFlush();
    },
    [enabled, responseId, scheduleImpressionFlush]
  );

  const onListingProductClick = useCallback(
    async (event, product) => {
      if (!enabled || !responseId || !product || product.isBanner) {
        return;
      }
      const dataComp = [...(event.target.attributes || [])].find(
        (attr) => attr.name === 'data-comp'
      );
      const productClicked =
        product &&
        (event.target.nodeName === 'svg' ||
          event.target.nodeName === 'use' ||
          dataComp?.value === 'AddToCart' ||
          dataComp?.value === 'ProductItemTitle' ||
          dataComp?.value === 'SeeDetails' ||
          dataComp?.value === 'OkendoStarsTotal' ||
          dataComp?.value === 'OkendoStarsTotalText' ||
          dataComp?.value === 'PictureImage');

      if (!productClicked) {
        return;
      }

      try {
        await sendListingBeaconV2({
          scope,
          eventType: 'clickthrough',
          responseId,
          results: [mapListingProductToBeaconResult(product)],
        });
      } catch (error) {
        console.error(error.message);
      }
    },
    [enabled, responseId, scope, sendListingBeaconV2]
  );

  return {
    onListingItemInView,
    onListingProductClick,
  };
};
